/**
 * Microsoft Entra ID authentication via MSAL.js.
 *
 * Wired to IDWTEAM's tenant by default; override via VITE_ENTRA_* env vars.
 * Tenant + client IDs are public identifiers, NOT secrets — they're meant to
 * ship in the bundle.
 *
 * Produces tokens that the API client attaches to every backend call.
 */
import {
  PublicClientApplication,
  EventType,
  BrowserAuthError,
  type Configuration,
  type AccountInfo
} from "@azure/msal-browser";
import type { AuthSession } from "@/types/user";

// Default to IDWTEAM tenant; override with VITE_ENTRA_TENANT_ID for other deployments
const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID || "f7615ef4-549b-4a4c-9318-017933c06a60";
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID || "2ab2f2b6-e0b7-49dd-9eba-08e2ae31c235";
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI || window.location.origin;

/**
 * Allowed email domains for client-side UX validation.
 * Users with emails outside this list are rejected before the Microsoft popup opens.
 *
 * NOTE: This is UX, not security. The real "no outsiders" guarantee comes from
 * the MSAL `authority` being set to a specific tenant ID below — Microsoft will
 * only authenticate users in that tenant. To override, set VITE_ALLOWED_DOMAINS
 * to a comma-separated list (or "*" for any domain).
 */
const ALLOWED_DOMAINS_RAW = import.meta.env.VITE_ALLOWED_DOMAINS || "idwteam.com,idwteam.in";
export const ALLOWED_DOMAINS: string[] = ALLOWED_DOMAINS_RAW
  .split(",")
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

/** Returns true if the email's domain is in the allowed list (or list is "*"). */
export function isEmailAllowed(email: string): boolean {
  if (!email || !email.includes("@")) return false;
  if (ALLOWED_DOMAINS.includes("*")) return true;
  const domain = email.split("@")[1].toLowerCase().trim();
  return ALLOWED_DOMAINS.includes(domain);
}

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      loggerCallback: () => {},
      piiLoggingEnabled: false,
      logLevel: 3
    }
  }
};

/** Scopes we request at sign-in time. */
export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
  prompt: "select_account"
};

/** Singleton MSAL instance. Call initialize() once before use. */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL and finalize any pending redirect-flow callback.
 * Must be called once at app startup before any other MSAL operation.
 *
 * After redirect-callback completes, also enforces the domain allowlist:
 * any account whose email isn't in ALLOWED_DOMAINS is signed out immediately.
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();

  // Enforce domain allowlist on whatever account(s) MSAL ended up with —
  // covers both fresh redirects and stale sessionStorage caches.
  const accounts = msalInstance.getAllAccounts();
  for (const acct of accounts) {
    const email = (acct.username || "").toLowerCase().trim();
    if (!isEmailAllowed(email)) {
      console.warn(
        `Cached account ${email} not in allowed domains; clearing.`
      );
      await msalInstance.clearCache();
      return;
    }
  }

  // If we have an allowed cached account, set it active
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }
}

/**
 * Pull a session object out of an MSAL account.
 */
export function accountToSession(account: AccountInfo | null): AuthSession | null {
  if (!account) return null;
  const email = account.username || "";
  const name = account.name || email;
  const initial = name
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "??";
  // homeAccountId format: <oid>.<tenantId>
  const tenantId = (account.homeAccountId || "").split(".")[1] || TENANT_ID;
  return { email, name, initial, tenantId };
}

/**
 * Trigger interactive sign-in via redirect flow.
 *
 * Why redirect not popup: popups are unreliable on deployed Azure Static Web Apps
 * because Cross-Origin-Opener-Policy blocks the popup→opener message after
 * Microsoft completes sign-in, surfacing as a misleading "user_cancelled" error.
 * Redirect navigates the whole tab to Microsoft and back, sidestepping COOP entirely.
 *
 * Behavior:
 *  - Calls loginRedirect(), which navigates the tab to login.microsoftonline.com.
 *  - The function does NOT return a session synchronously — the page is leaving.
 *  - When the user returns from Microsoft, initializeMsal()'s handleRedirectPromise()
 *    picks up the session and useAuth() updates accordingly.
 *  - Caller should show a "Redirecting…" state until the navigation kicks in.
 *
 * Throws synchronously on:
 *  - domain_not_allowed (email outside ALLOWED_DOMAINS)
 *  - redirect URI mismatch (Entra app not registered for this origin)
 *  - any other BrowserAuthError before redirect fires
 */
export async function signInWithMicrosoft(email?: string): Promise<AuthSession | null> {
  // Client-side guard before initiating the redirect
  if (email && !isEmailAllowed(email)) {
    const err = new Error(
      `Sign-in is restricted to ${ALLOWED_DOMAINS.join(", ")} email addresses.`
    ) as Error & { code?: string };
    err.code = "domain_not_allowed";
    throw err;
  }

  const request = {
    ...loginRequest,
    ...(email ? { loginHint: email } : {})
  };

  try {
    await msalInstance.loginRedirect(request);
    // Page is navigating away. Return null; the caller's spinner stays up
    // until the browser actually leaves the tab.
    return null;
  } catch (err) {
    // Redirect almost never fails before the navigation, but if it does
    // (misconfigured redirect URI being the most common cause), surface
    // a typed error to the UI.
    if (err instanceof BrowserAuthError) {
      const e = new Error(err.errorMessage) as Error & { code?: string };
      e.code = err.errorCode;
      throw e;
    }
    throw err;
  }
}

/**
 * Domain-allowlist guard for accounts returning from a redirect.
 * Call this from initializeMsal() after handleRedirectPromise resolves with
 * an account, OR from useAuth when the accounts list changes. If the returned
 * account's email isn't in ALLOWED_DOMAINS, we sign them out immediately.
 */
export async function enforceDomainAllowlist(account: AccountInfo): Promise<boolean> {
  const email = account.username || "";
  if (!isEmailAllowed(email)) {
    console.warn(
      `Account ${email} is not in an allowed domain (${ALLOWED_DOMAINS.join(", ")}); signing out.`
    );
    await msalInstance.clearCache();
    return false;
  }
  return true;
}

/**
 * Sign out via popup so we don't leave the app's tab.
 */
export async function signOut(): Promise<void> {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return;
  try {
    await msalInstance.logoutPopup({
      account,
      mainWindowRedirectUri: window.location.origin
    });
  } catch (err) {
    // Even if the logout call fails (popup blocked, etc.), we should clear
    // local state so the user gets back to the login page
    console.warn("Logout call failed, clearing local cache:", err);
    await msalInstance.clearCache();
  }
}

/**
 * Acquire a silent access token for backend calls. If silent refresh fails,
 * returns null — the caller decides whether to prompt for interactive sign-in.
 */
export async function getAccessToken(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return result.accessToken;
  } catch (err) {
    console.warn("Silent token acquisition failed:", err);
    return null;
  }
}

export { EventType };

